import React, { useState } from 'react';
import { X, Camera, Upload, Trash2, Search, Package, Loader2 } from 'lucide-react';
import DateTimePicker from '../../DateTimePicker';
import TextAreaPopup from '../../TextAreaPopup';
import treatmentReportsService from '../../../services/treatmentReportsService';

const TreatmentSection = ({
  formData,
  updateFormField
}) => {
  const [activePopup, setActivePopup] = useState(null);
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productSearch, setProductSearch] = useState('');

  // Parse products_used - stored as JSON array of { id, name }
  const selectedProducts = (() => {
    try {
      if (Array.isArray(formData.products_used)) return formData.products_used;
      if (typeof formData.products_used === 'string' && formData.products_used.startsWith('[')) {
        return JSON.parse(formData.products_used);
      }
    } catch (e) {}
    return [];
  })();

  const textareaFields = [
    { key: 'pest_findings', label: 'Visit Details & Observations', placeholder: 'Describe visit details and observations...' },
    { key: 'treatment_methods', label: 'Treatment Methods', placeholder: 'Describe the treatment methods used...' },
    { key: 'recommendations', label: 'Recommendations', placeholder: 'Recommendations for the customer...' },
  ];

  const loadProducts = async () => {
    if (allProducts.length > 0) return; // already loaded
    setLoadingProducts(true);
    try {
      const products = await treatmentReportsService.getTreatmentProducts();
      setAllProducts(products);
    } catch (error) {
      console.error('Error loading treatment products:', error);
    } finally {
      setLoadingProducts(false);
    }
  };

  const openProductPicker = () => {
    loadProducts();
    setProductSearch('');
    setShowProductPicker(true);
  };

  const toggleProduct = (product) => {
    const exists = selectedProducts.find(p => p.id === product.id);
    let updated;
    if (exists) {
      updated = selectedProducts.filter(p => p.id !== product.id);
    } else {
      updated = [...selectedProducts, { id: product.id, name: product.name, qty: 1 }];
    }
    updateFormField('products_used', updated);
  };

  const updateProductQty = (productId, newQty) => {
    const qty = Math.max(1, Math.min(30, newQty));
    const updated = selectedProducts.map(p => p.id === productId ? { ...p, qty } : p);
    updateFormField('products_used', updated);
  };

  const removeProduct = (productId) => {
    const updated = selectedProducts.filter(p => p.id !== productId);
    updateFormField('products_used', updated);
  };

  const isProductSelected = (productId) => selectedProducts.some(p => p.id === productId);
  const getProductQty = (productId) => selectedProducts.find(p => p.id === productId)?.qty || 1;

  // Filter and group products by category
  const filteredProducts = allProducts.filter(p => {
    if (!productSearch) return true;
    const q = productSearch.toLowerCase();
    return (p.name || '').toLowerCase().includes(q) ||
           (p.active_ingredients || '').toLowerCase().includes(q) ||
           (p.category_name || '').toLowerCase().includes(q);
  });

  const groupedProducts = {};
  filteredProducts.forEach(p => {
    const group = p.category_name || 'Other';
    if (!groupedProducts[group]) groupedProducts[group] = [];
    groupedProducts[group].push(p);
  });

  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    e.target.value = '';

    setUploadingPhoto(true);
    for (const file of files) {
      try {
        const result = await treatmentReportsService.uploadPhoto(file);
        const newPhoto = {
          id: Date.now() + Math.random().toString(36).substr(2, 9),
          url: result.url,
          name: result.name || file.name
        };
        updateFormField('photos', [...(formData.photos || []), newPhoto]);
      } catch (error) {
        console.error('Photo upload failed:', error);
      }
    }
    setUploadingPhoto(false);
  };

  const removePhoto = (photoId) => {
    updateFormField('photos', (formData.photos || []).filter(p => p.id !== photoId));
  };

  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
      <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-pest-blue">Treatment Details</h3>
      </div>

      <div className="p-6 space-y-4">
        {textareaFields.map(field => (
          <TextAreaPopup
            key={field.key}
            fieldKey={field.key}
            label={field.label}
            value={formData[field.key] || ''}
            onChange={(val) => updateFormField(field.key, val)}
            placeholder={field.placeholder}
            isOpen={activePopup === field.key}
            onOpen={() => setActivePopup(field.key)}
            onClose={() => setActivePopup(null)}
          />
        ))}

        {/* Products Used - picker */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Products Used</label>

          {/* Selected product tags */}
          {selectedProducts.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {selectedProducts.map(p => (
                <span key={p.id} className="inline-flex items-center gap-1 px-3 py-1.5 bg-teal-50 text-teal-700 text-sm font-medium rounded-lg border border-teal-200">
                  <Package size={14} />
                  {(p.qty || 1) > 1 && <span className="text-teal-500 font-bold">{p.qty}x</span>}
                  {p.name}
                  <button type="button" onClick={() => removeProduct(p.id)} className="text-teal-400 hover:text-red-500 ml-1">
                    <X size={14} />
                  </button>
                </span>
              ))}
            </div>
          )}

          <button
            type="button"
            onClick={openProductPicker}
            className={`w-full text-left px-4 py-3 border-2 rounded-lg text-sm transition-colors ${
              selectedProducts.length > 0
                ? 'border-gray-300 bg-white text-gray-600 hover:border-pest-blue'
                : 'border-dashed border-gray-300 text-gray-400 hover:border-pest-blue hover:text-gray-600'
            }`}
          >
            + {selectedProducts.length > 0 ? 'Add more products...' : 'Select products used...'}
          </button>
        </div>

        {/* Photos Section */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Photos</label>
          <div className="flex flex-wrap gap-3 mb-3">
            {(formData.photos || []).map((photo) => (
              <div key={photo.id} className="relative group w-24 h-24 rounded-lg overflow-hidden border-2 border-gray-200">
                <img src={photo.url || photo.data} alt={photo.name || 'Photo'} className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => removePhoto(photo.id)}
                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 shadow-sm"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
            {uploadingPhoto && (
              <div className="w-24 h-24 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center">
                <Loader2 size={20} className="animate-spin text-gray-400" />
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <label className={`flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-pest-blue to-pest-teal text-white rounded-lg text-sm font-medium cursor-pointer hover:from-blue-700 hover:to-teal-700 transition-all ${uploadingPhoto ? 'opacity-50 pointer-events-none' : ''}`}>
              <Camera size={16} />
              Take Photo
              <input type="file" accept="image/*" capture="environment" onChange={handlePhotoUpload} className="hidden" disabled={uploadingPhoto} />
            </label>
            <label className={`flex items-center gap-2 px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg text-sm font-medium cursor-pointer hover:border-gray-400 hover:bg-gray-50 transition-all ${uploadingPhoto ? 'opacity-50 pointer-events-none' : ''}`}>
              <Upload size={16} />
              Upload
              <input type="file" accept="image/*" multiple onChange={handlePhotoUpload} className="hidden" disabled={uploadingPhoto} />
            </label>
          </div>
        </div>

        {/* Follow-up */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.follow_up_required}
                onChange={(e) => updateFormField('follow_up_required', e.target.checked)}
                className="w-4 h-4 text-pest-blue border-2 border-gray-300 rounded focus:ring-pest-blue"
              />
              <span className="ml-3 text-sm font-medium text-gray-700">Follow-up Required</span>
            </label>

            {formData.follow_up_required && (
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">Follow-up Date</label>
                <DateTimePicker
                  value={formData.follow_up_date}
                  onChange={(val) => updateFormField('follow_up_date', val)}
                  className="w-full"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Product Picker Popup */}
      {showProductPicker && (
        <div className="fixed inset-0 bg-black bg-opacity-70 backdrop-blur-sm flex items-center justify-center z-[60] overflow-hidden" onClick={() => setShowProductPicker(false)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden m-4 flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex justify-between items-center p-4 md:p-6 border-b border-gray-200 bg-gradient-to-r from-pest-blue to-pest-teal">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-white">Products Used</h2>
                <p className="text-sm text-white/90 mt-1">
                  {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''} available
                  {selectedProducts.length > 0 && ` \u2022 ${selectedProducts.length} selected`}
                </p>
              </div>
              <button
                onClick={() => setShowProductPicker(false)}
                className="text-white hover:text-gray-200 transition-colors duration-200"
              >
                <X size={24} />
              </button>
            </div>

            {/* Search */}
            <div className="p-4 md:p-6 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue transition-colors"
                  placeholder="Search by product name, ingredients, or category..."
                  autoFocus
                />
              </div>
            </div>

            {/* Product List */}
            <div className="p-4 md:p-6 overflow-y-auto flex-1 overscroll-contain">
              {loadingProducts ? (
                <div className="text-center py-8 text-gray-400">Loading products...</div>
              ) : Object.keys(groupedProducts).length > 0 ? (
                <div className="space-y-4">
                  {Object.entries(groupedProducts).sort(([a], [b]) => a.localeCompare(b)).map(([group, products]) => (
                    <div key={group}>
                      <p className="text-xs uppercase tracking-wider text-gray-400 font-semibold mb-2 px-1">{group}</p>
                      <div className="space-y-1">
                        {products.map(product => {
                          const selected = isProductSelected(product.id);
                          const qty = getProductQty(product.id);
                          return (
                          <div
                            key={product.id}
                            className={`flex items-center gap-3 py-3 px-4 rounded-lg transition-all border-2 ${
                              selected
                                ? 'border-pest-teal bg-teal-50'
                                : 'border-transparent hover:bg-gray-50 hover:border-gray-200'
                            }`}
                          >
                            <label className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selected}
                                onChange={() => toggleProduct(product)}
                                className="rounded border-gray-300 text-pest-teal focus:ring-pest-teal w-4 h-4"
                              />
                              <Package size={16} className={selected ? 'text-pest-teal flex-shrink-0' : 'text-gray-400 flex-shrink-0'} />
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm font-medium truncate ${selected ? 'text-pest-teal' : 'text-gray-800'}`}>
                                  {product.name}
                                </p>
                                {product.active_ingredients && (
                                  <p className="text-xs text-gray-400">{product.active_ingredients}</p>
                                )}
                              </div>
                            </label>
                            {selected && (
                              <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                                <button
                                  type="button"
                                  onClick={() => updateProductQty(product.id, qty - 1)}
                                  disabled={qty <= 1}
                                  className="w-7 h-7 rounded-md bg-white border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center text-sm font-bold"
                                >
                                  −
                                </button>
                                <span className="w-8 text-center text-sm font-bold text-pest-teal">{qty}</span>
                                <button
                                  type="button"
                                  onClick={() => updateProductQty(product.id, qty + 1)}
                                  disabled={qty >= 30}
                                  className="w-7 h-7 rounded-md bg-white border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center text-sm font-bold"
                                >
                                  +
                                </button>
                              </div>
                            )}
                          </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ) : productSearch ? (
                <p className="text-center text-gray-400 py-8">No products match your search.</p>
              ) : (
                <p className="text-center text-gray-400 py-8">No products available.</p>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 md:p-6 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                {selectedProducts.length} product{selectedProducts.length !== 1 ? 's' : ''} selected
              </p>
              <button
                type="button"
                onClick={() => setShowProductPicker(false)}
                className="px-6 py-2.5 bg-gradient-to-r from-pest-blue to-pest-teal text-white rounded-lg hover:from-blue-700 hover:to-teal-700 font-medium transition-all"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TreatmentSection;
